import { ELegalIdType, EVerticalNames, REGIONS } from '@constants';
import { IUser } from '@sdk/contracts';
import { Col, DatePicker, Form, FormInstance, Input, Row, Select } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CountrySelector,
  HubspotContactSelector,
  LegalIdTypeSelector,
  TagSelector,
  SecondLastNameField,
  PhoneInput,
  ProvincesExtendedSelector,
  StreetTypeSelector,
  AddressAutocomplete
} from '../Form';
import { EmailInput } from '../Form/EmailInput';
import { StudentFPGeneralInfoForm } from '../StudentFPGeneralInfoForm';
import { WarningMessage } from '../WarningMessage';

import { DocumentInputWrapper, SectionTitle } from './StudentGeneralInfoForm.styles';

import { RULES } from '@/constants';
import {
  useAddressFormHandler,
  useFullAddress,
  useLegalIdValidation,
  useRequiredIfHadValue,
  useTagsChangedWarning
} from '@/hooks';
import { useProfile } from '@/store';

type StudentGeneralInfoFormProps = {
  form: FormInstance<IUser>;
};

export const StudentGeneralInfoForm: React.FC<StudentGeneralInfoFormProps> = ({ form }) => {
  const { t } = useTranslation();
  const {
    roleCheck: { isSales },
    profile
  } = useProfile();
  const { tagsChanged } = useTagsChangedWarning();
  const { handleAddressSelect } = useAddressFormHandler(form);

  const userId = Form.useWatch('_id', form);
  const userInfo = Form.useWatch('userInfo', form);
  const formRegionConfig = Form.useWatch('regionConfig', form);
  const isFPStudent = profile?.academy.slug === EVerticalNames.FP;

  const region = formRegionConfig ?? profile?.regionConfig;

  const fullAddress = useFullAddress(userInfo);
  const { legalIdRule } = useLegalIdValidation({ form, region });

  const { getFieldRules, isFieldRequired } = useRequiredIfHadValue({
    form,
    userId,
    excludeFields: [
      'hubspotId',
      ['userInfo', 'socialSecurityNumber'],
      ['userInfo', 'address'],
      ['userInfo', 'streetNumber'],
      ['userInfo', 'staircase'],
      ['userInfo', 'floor'],
      ['userInfo', 'door'],
      ['userInfo', 'postalCode']
    ]
  });

  const handleLegalIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const upperValue = e.target.value.toUpperCase();
    form.setFieldValue('legalId', upperValue);
  };

  const handleAddressClear = useCallback(() => {
    form.setFieldsValue({
      userInfo: {
        province: undefined,
        city: undefined,
        residenceLocality: undefined,
        streetType: undefined,
        address: undefined,
        streetNumber: undefined,
        staircase: undefined,
        floor: undefined,
        door: undefined,
        postalCode: undefined
      }
    });
  }, [form]);

  return (
    <>
      <Form.Item name="_id" hidden>
        <Input />
      </Form.Item>
      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item
            name="hubspotId"
            label={t('HUBSPOT_CONTACT')}
            rules={isSales ? [RULES.REQUIRED] : undefined}>
            <HubspotContactSelector />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item name="tags" label={t('TAGS')} rules={[RULES.REQUIRED]}>
            <TagSelector mode="multiple" style={{ maxWidth: '100%' }} />
          </Form.Item>
          {tagsChanged && (
            <WarningMessage
              title={t('WARNING_CHANGING_STUDENT_TAGS')}
              text={t('WARNING_CHANGING_STUDENT_TAGS_TEXT')}
            />
          )}
        </Col>
      </Row>
      <Row gutter={8}>
        <Col xs={24} xl={12}>
          <Form.Item name="name" label={t('NAME')} rules={[RULES.REQUIRED]}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} xl={12}>
          <Form.Item name="lastName" label={t('LAST_NAME')} rules={[RULES.REQUIRED]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <SecondLastNameField
            labelSecondLastName={t('SECOND_LASTNAME')}
            labelCheckbox={t('IT_HAS_NO_SECOND_LASTNAME')}
          />
        </Col>
      </Row>
      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <EmailInput label={t('EMAIL')} />
        </Col>
      </Row>

      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item name="phone" label={t('PHONE')} rules={getFieldRules('phone')}>
            <PhoneInput />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item
            label={t('IDENTIFICATION_DOCUMENT')}
            required={isFieldRequired('legalId') || isFieldRequired('legalIdType')}>
            <DocumentInputWrapper>
              <Form.Item
                name="legalIdType"
                className="type-selector"
                rules={getFieldRules('legalIdType')}
                initialValue={ELegalIdType.DNI_NIF_NIE}
                noStyle>
                <LegalIdTypeSelector region={region} />
              </Form.Item>
              <Form.Item
                name="legalId"
                className="number-input"
                noStyle
                dependencies={['legalIdType']}
                rules={[...getFieldRules('legalId'), legalIdRule]}>
                <Input
                  onChange={handleLegalIdChange}
                  placeholder={t('DOCUMENT_NUMBER_PLACEHOLDER')}
                />
              </Form.Item>
            </DocumentInputWrapper>
          </Form.Item>
        </Col>
      </Row>
      {!isFPStudent && (
        <Row gutter={8}>
          <Col xs={24} xl={12}>
            <Form.Item
              name="birthday"
              label={t('BIRTHDAY')}
              rules={getFieldRules('birthday')}
              getValueProps={value => ({ value: value ? dayjs(value) : null })}>
              <DatePicker
                format="DD/MM/YYYY"
                onChange={value => form.setFieldValue('birthday', value?.toDate())}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item noStyle shouldUpdate={(prev, curr) => prev._id !== curr._id}>
            {formInstance => {
              const isEdit = !!formInstance.getFieldValue('_id');
              if (isEdit) return null;
              return (
                <Form.Item name="regionConfig" label={t('REGION')} rules={[RULES.REQUIRED]}>
                  <Select
                    options={REGIONS.map(value => ({ label: t(`REGION_${value}`), value }))}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
      </Row>
      {!isFPStudent && (
        <Row gutter={8}>
          <Col xs={24} xl={12}>
            <Form.Item
              name={['userInfo', 'countryBirth']}
              label={t('BORN_COUNTRY')}
              rules={getFieldRules(['userInfo', 'countryBirth'])}>
              <CountrySelector disableDescription placeholder={t('SELECT_ONE_OPTION')} />
            </Form.Item>
          </Col>
          <Col xs={24} xl={12}>
            <Form.Item
              name={['userInfo', 'countryResidence']}
              label={t('RESIDENCE_COUNTRY')}
              rules={getFieldRules(['userInfo', 'countryResidence'])}>
              <CountrySelector disableDescription placeholder={t('SELECT_ONE_OPTION')} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {isFPStudent && <StudentFPGeneralInfoForm form={form} />}
      <SectionTitle>{t('ADDRESS_DATA')}</SectionTitle>

      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item label={t('DefaultTranslations:SEARCH_ADDRESS')}>
            <AddressAutocomplete
              initialValue={fullAddress}
              onAddressSelect={handleAddressSelect}
              onClear={handleAddressClear}
              placeholder={t('DefaultTranslations:SEARCH_ADDRESS')}
            />
          </Form.Item>
        </Col>
      </Row>

      {userInfo?.address && (
        <>
          <Row gutter={8}>
            <Col xs={24} xl={24}>
              <Form.Item
                name={['userInfo', 'streetType']}
                label={t('STREET_TYPE')}
                rules={getFieldRules(['userInfo', 'streetType'])}>
                <StreetTypeSelector />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col xs={24} xl={12}>
              <Form.Item
                name={['userInfo', 'address']}
                label={t('StudentProfileConfigurationTranslations:STREET_NAME')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} xl={12}>
              <Form.Item
                name={['userInfo', 'streetNumber']}
                label={t('StudentProfileConfigurationTranslations:STREET_NUMBER')}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col xs={24} xl={6}>
              <Form.Item
                name={['userInfo', 'staircase']}
                label={t('StudentProfileConfigurationTranslations:STAIRCASE')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} xl={6}>
              <Form.Item
                name={['userInfo', 'floor']}
                label={t('StudentProfileConfigurationTranslations:FLOOR')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} xl={6}>
              <Form.Item
                name={['userInfo', 'door']}
                label={t('StudentProfileConfigurationTranslations:DOOR')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} xl={6}>
              <Form.Item
                name={['userInfo', 'postalCode']}
                label={t('StudentProfileConfigurationTranslations:POSTAL_CODE')}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col xs={24} xl={12}>
              <Form.Item
                name={['userInfo', 'province']}
                label={t('PROVINCE')}
                rules={getFieldRules(['userInfo', 'province'])}>
                <ProvincesExtendedSelector />
              </Form.Item>
            </Col>
            <Col xs={24} xl={12}>
              <Form.Item
                name={['userInfo', 'city']}
                label={t('MUNICIPALITY')}
                rules={getFieldRules(['userInfo', 'city'])}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col xs={24} xl={24}>
              <Form.Item
                name={['userInfo', 'residenceLocality']}
                label={t('LOCALITY')}
                rules={getFieldRules(['userInfo', 'residenceLocality'])}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};
